import { Check, Circle, CircleX, Voicemail } from "lucide-react";
import { Spinner } from "@/components/ui/feedback";
import type { DialStep, StripPart } from "@/lib/dialer";
import { cn } from "@/lib/utils";

/**
 * A dial as one row on the Phone page: the call's attempts left to right, and the one being made
 * opened up into its steps. One component for every call that is on its way, so they all read the
 * same: a call the dialer places (`dialStrip` in `lib/dialer.ts`, drawn by
 * `softphone/dialer-offer.tsx`) and a number the rep dialed by hand (`softphone/manual-call.tsx`).
 *
 * An attempt that is over is a dimmed capsule that says how it went. The one being made is the
 * capsule in the accent tone, as wide as the row leaves it: its place in the run ("Redial 1 of
 * 2"), then its steps on a line, done, in progress and to come. The attempts that would follow
 * are dashed, since they are made only if nobody answers. A call with no redials is the one
 * capsule of steps. A dial the dialer ended on a voicemail stops at its step, and its capsule
 * turns to the warning tone with the steps after it left undone.
 *
 * It wraps instead of squeezing: an attempt that does not fit beside the others takes the next
 * line whole. In a row too narrow for every step's name (`@container/dial` on the stage) only the
 * step in progress is named, and the others are their marks, each with its name as a tooltip.
 */
export function DialStrip({ parts }: { parts: StripPart[] }) {
  return (
    <ol className="flex flex-wrap items-stretch gap-1.5" aria-label="How far the call has got">
      {parts.map((part) => {
        if (part.kind === "made") {
          return (
            <li
              key={part.label}
              className="flex min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap rounded-lg bg-surface-sunken px-2.5 py-1.5 text-xs text-text-muted"
            >
              <CircleX className="size-3.5 shrink-0" aria-hidden />
              <span className="shrink-0 font-semibold">{part.label}</span>
              {/* The campaign's own word for the outcome, which can be a long one. */}
              <span className="truncate" title={part.text}>
                {part.text}
              </span>
            </li>
          );
        }
        if (part.kind === "next") {
          return (
            <li
              key={part.label}
              title="Made only if nobody answers"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed border-border-strong px-2.5 py-1.5 text-xs"
            >
              <Circle className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
              <span className="font-semibold text-text">{part.label}</span>
              {part.text && <span className="text-text-muted">{part.text}</span>}
            </li>
          );
        }
        const halted = part.steps.some((step) => step.state === "stopped");
        return (
          <li
            key="now"
            aria-current={parts.length > 1 ? "step" : undefined}
            className={cn(
              "flex min-w-0 flex-auto items-center gap-2.5 rounded-lg border px-2.5 py-1.5",
              halted ? "border-warning-9 bg-warning-2" : "border-accent-8 bg-accent-2",
            )}
          >
            {part.label && (
              <>
                <span className="shrink-0 text-xs font-semibold text-text">{part.label}</span>
                <span
                  aria-hidden
                  className={cn("h-4 w-px shrink-0", halted ? "bg-warning-9" : "bg-accent-6")}
                />
              </>
            )}
            <ol className="flex min-w-0 flex-1 items-center gap-1.5">
              {part.steps.map((step, i) => (
                <li
                  key={step.label}
                  // The name is the item's own, since the words under it are not always drawn.
                  aria-label={step.label}
                  title={step.label}
                  aria-current={step.state === "current" ? "step" : undefined}
                  className={cn("flex min-w-0 items-center gap-1.5", i > 0 && "flex-auto")}
                >
                  {/* The line from the step before, coloured once the dial has passed along it. */}
                  {i > 0 && (
                    <span
                      aria-hidden
                      className={cn(
                        "h-0.5 min-w-2 flex-1 rounded-full @2xl/dial:min-w-3",
                        step.state === "todo" ? "bg-border-strong" : "bg-success-9",
                      )}
                    />
                  )}
                  <StepMark state={step.state} />
                  <span
                    aria-hidden
                    className={cn(
                      "truncate text-xs",
                      step.state === "todo" && "text-text-muted",
                      step.state === "done" && "text-text",
                      step.state === "current" && "font-semibold text-text",
                      step.state === "stopped" && "font-semibold text-warning-11",
                      // Named always while it is the step in progress or the one the dial ended
                      // on; the rest only where the row has the room.
                      step.state !== "current" && step.state !== "stopped" && "hidden @2xl/dial:block",
                    )}
                  >
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </li>
        );
      })}
    </ol>
  );
}

/** A step's mark on the strip: passed, in progress, the one a dial ended on, or still to come. */
function StepMark({ state }: { state: DialStep["state"] }) {
  if (state === "current") return <Spinner className="size-4 shrink-0 text-accent-11" />;
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]",
        state === "done" && "border-success-9 bg-success-9 text-white",
        state === "stopped" && "border-warning-9 bg-warning-4 text-warning-11",
        state === "todo" && "border-border-strong bg-surface",
      )}
    >
      {state === "done" ? (
        <Check className="size-2.5" strokeWidth={3} />
      ) : state === "stopped" ? (
        <Voicemail className="size-2.5" />
      ) : null}
    </span>
  );
}
